import { useState } from 'react';

// Money formatters pinned to en-US with thousand separators. Bare
// `.toFixed(0)` printed "$12345" instead of "$12,345"; for a CEO's reports
// the grouped form is far easier to read (and consistent with the rest of
// the app — see utils.fmtMoney / PR #460).
const money0 = (n) => Number(n || 0).toLocaleString('en-US', { maximumFractionDigits: 0 });
const money2 = (n) => Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export function MetricsView({ clients, policies, leads, commissions, expenses, tasks, users, timelog, currentUser, lang }) {
  const es = lang === "es";
  const [period, setPeriod] = useState("ytd"); // ytd | q1 | q2 | q3 | q4 | all
  const [tab, setTab] = useState("overview"); // overview | growth | team | financial

  const allClients = clients || [];
  const allPolicies = policies || [];
  const allLeads = leads || [];
  const allComm = commissions || [];
  const allExp = expenses || [];
  const allTasks = tasks || [];
  const allTime = timelog || [];

  // ── Helpers ──
  const now = new Date();
  const yr = now.getFullYear();
  const monthNames = es
    ? ["Ene","Feb","Mar","Abr","May","Jun","Jul","Ago","Sep","Oct","Nov","Dic"]
    : ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

  // Pin date-only strings ('YYYY-MM-DD') to LOCAL midnight before reading
  // month/year. new Date('2026-06-01') parses as UTC midnight, so .getMonth()
  // in Florida (UTC-4/-5) returned the PREVIOUS month for day-1 dates,
  // mis-bucketing policies/expenses (feedback_local_day_not_utc). Full ISO
  // timestamps (client createdAt) are parsed as-is.
  const parseLocal = (dateStr) => /^\d{4}-\d{2}-\d{2}$/.test(dateStr) ? new Date(dateStr + 'T00:00:00') : new Date(dateStr);
  const getMonth = (dateStr) => { if (!dateStr) return -1; const d = parseLocal(dateStr); return isNaN(d.getTime()) ? -1 : d.getMonth(); };
  const getYear = (dateStr) => { if (!dateStr) return 0; const d = parseLocal(dateStr); return isNaN(d.getTime()) ? 0 : d.getFullYear(); };
  // Parse money that may arrive as a formatted string ("$1,200"); plain
  // parseFloat("1,200") silently returns 1 and under-counts totals.
  const num = (v) => Number(String(v ?? '').replace(/[^0-9.-]/g, '')) || 0;
  const inPeriod = (dateStr) => {
    if (!dateStr) return false;
    const y = getYear(dateStr); const m = getMonth(dateStr);
    if (period === "all") return true;
    if (y !== yr) return false;
    if (period === "ytd") return true;
    if (period === "q1") return m >= 0 && m <= 2;
    if (period === "q2") return m >= 3 && m <= 5;
    if (period === "q3") return m >= 6 && m <= 8;
    if (period === "q4") return m >= 9 && m <= 11;
    return false;
  };

  // ── Core Metrics ──
  const activeClients = allClients.filter(c => c.status === "active").length;
  const activePolicies = allPolicies.filter(p => p.status === "active").length;
  const totalPremium = allPolicies.filter(p => p.status === "active").reduce((s, p) => s + num(p.monthlyPremium), 0);
  const annualPremium = totalPremium * 12;
  const avgPolicyValue = activePolicies > 0 ? totalPremium / activePolicies : 0;
  const policiesPerClient = activeClients > 0 ? (activePolicies / activeClients).toFixed(1) : "0";

  const newClientsThisYear = allClients.filter(c => getYear(c.createdAt) === yr).length;
  const convertedLeads = allLeads.filter(l => l.status === "converted").length;
  const totalLeads = allLeads.length;
  const conversionRate = totalLeads > 0 ? ((convertedLeads / totalLeads) * 100).toFixed(0) : "0";

  const pendingTasks = allTasks.filter(t => t.status !== "done").length;
  const completedTasks = allTasks.filter(t => t.status === "done").length;
  const taskCompletionRate = (completedTasks + pendingTasks) > 0 ? ((completedTasks / (completedTasks + pendingTasks)) * 100).toFixed(0) : "0";

  const totalCommissions = allComm.reduce((s, c) => s + num(c.amount), 0);
  const totalExpenses = allExp.reduce((s, e) => s + num(e.amount), 0);
  const netProfit = totalCommissions - totalExpenses;

  // ── Monthly Data for Charts ──
  const monthlyData = monthNames.map((name, i) => {
    const newCl = allClients.filter(c => getYear(c.createdAt) === yr && getMonth(c.createdAt) === i).length;
    const newPol = allPolicies.filter(p => getYear(p.effectiveDate) === yr && getMonth(p.effectiveDate) === i).length;
    const comm = allComm.filter(c => c.month && c.month.startsWith(`${yr}-${String(i + 1).padStart(2, "0")}`)).reduce((s, c) => s + num(c.amount), 0);
    const exp = allExp.filter(e => getYear(e.date) === yr && getMonth(e.date) === i).reduce((s, e) => s + num(e.amount), 0);
    return { name, newClients: newCl, newPolicies: newPol, commissions: comm, expenses: exp, month: i };
  });

  // Cumulative clients
  let cumClients = allClients.filter(c => getYear(c.createdAt) < yr).length;
  monthlyData.forEach(m => { cumClients += m.newClients; m.cumClients = cumClients; });

  let cumPolicies = allPolicies.filter(p => getYear(p.effectiveDate) < yr).length;
  monthlyData.forEach(m => { cumPolicies += m.newPolicies; m.cumPolicies = cumPolicies; });

  // ── Policy Mix ──
  const PTYPES = { aca:"ACA",ma:"Medicare Adv",medsup:"Med Supp",partd:"Part D",life:"Life",dental:"Dental",vision:"Vision",home:"Home",auto:"Auto",renters:"Renters",commercial:"Commercial",umbrella:"Umbrella" };
  const policyMix = Object.entries(PTYPES).map(([k, label]) => {
    const count = allPolicies.filter(p => p.type === k && p.status === "active").length;
    return { type: k, label, count };
  }).filter(p => p.count > 0).sort((a, b) => b.count - a.count);
  const maxPolicyCount = Math.max(...policyMix.map(p => p.count), 1);
  const policyColors = { aca:"#00d4ff",ma:"#06d6a0",medsup:"#48cae4",partd:"#818cf8",life:"#f472b6",dental:"#fbbf24",vision:"#a78bfa",home:"#FF6B35",auto:"#ffb700",renters:"#00ff88",commercial:"#c084fc",umbrella:"#06d6a0" };

  // ── Top Carriers ──
  const carrierMap = {};
  allPolicies.filter(p => p.status === "active").forEach(p => { if (p.carrier) { carrierMap[p.carrier] = (carrierMap[p.carrier] || 0) + 1; } });
  const topCarriers = Object.entries(carrierMap).map(([name, count]) => ({ name, count })).sort((a, b) => b.count - a.count).slice(0, 8);
  const maxCarrier = Math.max(...topCarriers.map(c => c.count), 1);

  // ── Lead Sources ──
  const sourceMap = {};
  allLeads.forEach(l => { const s = l.source || "unknown"; sourceMap[s] = (sourceMap[s] || 0) + 1; });
  const leadSources = Object.entries(sourceMap).map(([name, count]) => ({ name, count })).sort((a, b) => b.count - a.count);
  const sourceColors = { website:"#00d4ff", referral:"#06d6a0", cold_call:"#ffb700", event:"#f472b6", social:"#c084fc", unknown:"#8eafc2" };

  // ── Renewal Health ──
  // Whole-calendar-day diff in LOCAL time. Raw `new Date('YYYY-MM-DD')` parses
  // as UTC midnight, so subtracting the local `now` was off by the UTC offset
  // (Florida -4/-5h) and could mis-bucket a renewal due today as "expired"
  // (feedback_local_day_not_utc).
  const daysUntilLocal = (d) => {
    if (!d) return null;
    const target = /^\d{4}-\d{2}-\d{2}$/.test(d) ? new Date(d + 'T00:00:00') : new Date(d);
    if (isNaN(target.getTime())) return null;
    const a = new Date(target.getFullYear(), target.getMonth(), target.getDate());
    const b = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    return Math.round((a - b) / 86400000);
  };
  const renewingSoon = allPolicies.filter(p => { if (!p.renewalDate || p.status !== "active") return false; const d = daysUntilLocal(p.renewalDate); return d !== null && d >= 0 && d <= 90; }).length;
  const expiredPolicies = allPolicies.filter(p => { if (!p.renewalDate || p.status !== "active") return false; const d = daysUntilLocal(p.renewalDate); return d !== null && d < 0; }).length;

  // ── Simple Bar Chart Component ──
  const BarChart = ({ data, dataKey, color, label, maxVal, showLabels = true, fmtVal = (v) => v }) => {
    const max = maxVal || Math.max(...data.map(d => d[dataKey] || 0), 1);
    return (
      <div>
        {label && <div style={{ fontSize: ".65rem", color: "var(--c-text3,#45688a)", fontWeight: 700, textTransform: "uppercase", letterSpacing: ".08em", fontFamily: "var(--font-mono)", marginBottom: ".4rem" }}>{label}</div>}
        <div style={{ display: "flex", alignItems: "flex-end", gap: 3, height: 100 }}>
          {data.map((d, i) => {
            const val = d[dataKey] || 0;
            const h = max > 0 ? (val / max) * 80 : 0;
            return (
              <div key={i} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
                {val > 0 && <div style={{ fontSize: ".55rem", color, fontWeight: 700, fontFamily: "var(--font-mono)" }}>{fmtVal(val)}</div>}
                <div style={{ width: "100%", height: h, borderRadius: "4px 4px 0 0", background: `linear-gradient(180deg, ${color}, ${color}66)`, minHeight: val > 0 ? 4 : 0, transition: "height .3s" }} />
                {showLabels && <div style={{ fontSize: ".5rem", color: "var(--c-text3,#45688a)", fontFamily: "var(--font-mono)" }}>{d.name}</div>}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // ── Horizontal Bar ──
  const HBar = ({ items, maxVal, colorMap, colorDefault }) => (
    <div style={{ display: "flex", flexDirection: "column", gap: ".35rem" }}>
      {items.map((item, i) => (
        <div key={i}>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 2 }}>
            <span style={{ fontSize: ".72rem", color: "var(--c-white,#eef6ff)", fontWeight: 600 }}>{item.label || item.name}</span>
            <span style={{ fontSize: ".72rem", color: (colorMap && colorMap[item.type || item.name]) || colorDefault || "var(--c-cyan,#00d4ff)", fontWeight: 800, fontFamily: "var(--font-mono)" }}>{item.count}</span>
          </div>
          <div style={{ height: 6, borderRadius: 3, background: "var(--c-surface3,#162d44)", overflow: "hidden" }}>
            <div style={{ height: "100%", borderRadius: 3, background: (colorMap && colorMap[item.type || item.name]) || colorDefault || "var(--c-cyan,#00d4ff)", width: `${(item.count / maxVal) * 100}%`, transition: "width .3s" }} />
          </div>
        </div>
      ))}
    </div>
  );

  // ── KPI Card ──
  const KPI = ({ icon, value, label, color, sub }) => (
    <div style={{ background: "var(--c-surface,#0d1c2e)", border: "1px solid var(--c-border,#1a3550)", borderRadius: 12, padding: ".75rem .875rem", position: "relative", overflow: "hidden" }}>
      <div style={{ position: "absolute", top: -8, right: -8, fontSize: "2.5rem", opacity: .06 }}>{icon}</div>
      <div style={{ fontSize: ".95rem", marginBottom: ".2rem" }}>{icon}</div>
      <div style={{ fontFamily: "var(--font-display)", fontSize: "1.5rem", fontWeight: 800, color }}>{value}</div>
      <div style={{ fontSize: ".62rem", color: "var(--c-text3,#45688a)", fontWeight: 600, textTransform: "uppercase", letterSpacing: ".08em", fontFamily: "var(--font-mono)" }}>{label}</div>
      {sub && <div style={{ fontSize: ".6rem", color: "var(--c-text2,#7aa3c0)", marginTop: 2 }}>{sub}</div>}
    </div>
  );

  return (
    <div>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "1rem", flexWrap: "wrap", gap: ".5rem" }}>
        <div style={{ display: "flex", alignItems: "center", gap: ".5rem" }}>
          <span style={{ fontSize: "1.3rem" }}></span>
          <h2 style={{ fontSize: "1.1rem", fontWeight: 800, color: "var(--c-white,#eef6ff)", margin: 0 }}>{es ? "Métricas y Analytics" : "Metrics & Analytics"}</h2>
        </div>
        <div style={{ display: "flex", gap: ".3rem", flexWrap: "wrap" }}>
          {[["ytd","YTD"],["q1","Q1"],["q2","Q2"],["q3","Q3"],["q4","Q4"],["all",es?"Todo":"All"]].map(([id,label]) => (
            <button type="button" key={id} onClick={() => setPeriod(id)}
              style={{ padding: ".3rem .6rem", borderRadius: 7, border: `1px solid ${period===id?"rgba(0,212,255,.4)":"var(--c-border,#1a3550)"}`, background: period===id?"var(--c-cyan-dim)":"transparent", color: period===id?"var(--c-cyan,#00d4ff)":"var(--c-text3,#45688a)", fontSize: ".7rem", fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: "flex", gap: ".35rem", marginBottom: "1rem", flexWrap: "wrap" }}>
        {[["overview",es?"General":"Overview"],["growth",es?"Crecimiento":"Growth"],["team",es?"Equipo":"Team"],["financial",es?"Financiero":"Financial"]].map(([id,label]) => (
          <button type="button" key={id} onClick={() => setTab(id)}
            style={{ padding: ".4rem .85rem", borderRadius: 8, border: `1px solid ${tab===id?"rgba(0,212,255,.4)":"var(--c-border,#1a3550)"}`, background: tab===id?"var(--c-cyan-dim)":"transparent", color: tab===id?"var(--c-cyan,#00d4ff)":"var(--c-text3,#45688a)", fontSize: ".75rem", fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>
            {label}
          </button>
        ))}
      </div>

      {/* ══════ OVERVIEW TAB ══════ */}
      {tab === "overview" && (<>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(150px,1fr))", gap: ".5rem", marginBottom: "1rem" }}>
          <KPI icon="" value={activeClients} label={es?"Clientes Activos":"Active Clients"} color="var(--c-cyan,#00d4ff)" sub={`+${newClientsThisYear} ${es?"este año":"this year"}`} />
          <KPI icon="" value={activePolicies} label={es?"Pólizas Activas":"Active Policies"} color="var(--c-green,#00ff88)" sub={`${policiesPerClient} ${es?"por cliente":"per client"}`} />
          <KPI icon="" value={`$${money0(totalPremium)}`} label={es?"Premium Mensual":"Monthly Premium"} color="var(--c-cyan,#00d4ff)" sub={`$${money0(annualPremium)}/yr`} />
          <KPI icon="" value={`${conversionRate}%`} label={es?"Tasa Conversión":"Conversion Rate"} color="#f472b6" sub={`${convertedLeads}/${totalLeads} leads`} />
          <KPI icon="" value={renewingSoon} label={es?"Renovación <90d":"Renewing <90d"} color="var(--c-amber,#ffb700)" sub={expiredPolicies > 0 ? `${expiredPolicies} ${es?"vencidas":"expired"}` : "OK"} />
          <KPI icon="" value={`${taskCompletionRate}%`} label={es?"Tasks Completadas":"Task Completion"} color="var(--c-green,#00ff88)" sub={`${pendingTasks} ${es?"pendientes":"pending"}`} />
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: ".75rem" }}>
          {/* Policy Mix */}
          <div style={{ background: "var(--c-surface,#0d1c2e)", border: "1px solid var(--c-border,#1a3550)", borderRadius: 12, padding: "1rem" }}>
            <div style={{ fontSize: ".7rem", fontWeight: 700, color: "var(--c-cyan,#00d4ff)", textTransform: "uppercase", letterSpacing: ".08em", fontFamily: "var(--font-mono)", marginBottom: ".65rem" }}>{es ? "Mix de Pólizas" : "Policy Mix"}</div>
            <HBar items={policyMix} maxVal={maxPolicyCount} colorMap={policyColors} />
          </div>

          {/* Top Carriers */}
          <div style={{ background: "var(--c-surface,#0d1c2e)", border: "1px solid var(--c-border,#1a3550)", borderRadius: 12, padding: "1rem" }}>
            <div style={{ fontSize: ".7rem", fontWeight: 700, color: "var(--c-green,#00ff88)", textTransform: "uppercase", letterSpacing: ".08em", fontFamily: "var(--font-mono)", marginBottom: ".65rem" }}>{es ? "Top Carriers" : "Top Carriers"}</div>
            <HBar items={topCarriers} maxVal={maxCarrier} colorDefault="var(--c-green,#00ff88)" />
          </div>

          {/* Lead Sources */}
          <div style={{ background: "var(--c-surface,#0d1c2e)", border: "1px solid var(--c-border,#1a3550)", borderRadius: 12, padding: "1rem" }}>
            <div style={{ fontSize: ".7rem", fontWeight: 700, color: "#f472b6", textTransform: "uppercase", letterSpacing: ".08em", fontFamily: "var(--font-mono)", marginBottom: ".65rem" }}>{es ? "Fuentes de Leads" : "Lead Sources"}</div>
            <HBar items={leadSources} maxVal={Math.max(...leadSources.map(l => l.count), 1)} colorMap={sourceColors} colorDefault="#f472b6" />
          </div>

          {/* Quick Stats */}
          <div style={{ background: "var(--c-surface,#0d1c2e)", border: "1px solid var(--c-border,#1a3550)", borderRadius: 12, padding: "1rem" }}>
            <div style={{ fontSize: ".7rem", fontWeight: 700, color: "var(--c-amber,#ffb700)", textTransform: "uppercase", letterSpacing: ".08em", fontFamily: "var(--font-mono)", marginBottom: ".65rem" }}>{es ? "Indicadores Clave" : "Key Indicators"}</div>
            {[
              { l: es?"Avg Premium/Póliza":"Avg Premium/Policy", v: `$${money2(avgPolicyValue)}/mo`, c: "var(--c-cyan,#00d4ff)" },
              { l: es?"Pólizas por Cliente":"Policies per Client", v: policiesPerClient, c: "var(--c-green,#00ff88)" },
              { l: es?"Clientes Health vs P&C":"Health vs P&C Clients", v: `${allPolicies.filter(p=>["aca","ma","medsup","partd","dental","vision"].includes(p.type)&&p.status==="active").length} / ${allPolicies.filter(p=>["home","auto","renters","commercial","umbrella"].includes(p.type)&&p.status==="active").length}`, c: "var(--c-amber,#ffb700)" },
              { l: es?"Leads este Mes":"Leads this Month", v: allLeads.filter(l=>getYear(l.createdAt)===yr&&getMonth(l.createdAt)===now.getMonth()).length, c: "#f472b6" },
              { l: es?"Tasa de Retención":"Retention Rate", v: allClients.length > 0 ? `${((activeClients/allClients.length)*100).toFixed(0)}%` : "N/A", c: "#c084fc" },
            ].map((item,i) => (
              <div key={i} style={{ display: "flex", justifyContent: "space-between", padding: ".4rem .5rem", background: i%2===0?"var(--c-surface2,#112538)":"transparent", borderRadius: 6, marginBottom: 2 }}>
                <span style={{ fontSize: ".72rem", color: "var(--c-text2,#7aa3c0)" }}>{item.l}</span>
                <span style={{ fontSize: ".78rem", fontWeight: 800, color: item.c, fontFamily: "var(--font-display)" }}>{item.v}</span>
              </div>
            ))}
          </div>
        </div>
      </>)}

      {/* ══════ GROWTH TAB ══════ */}
      {tab === "growth" && (<>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: ".75rem", marginBottom: "1rem" }}>
          <div style={{ background: "var(--c-surface,#0d1c2e)", border: "1px solid var(--c-border,#1a3550)", borderRadius: 12, padding: "1rem" }}>
            <BarChart data={monthlyData} dataKey="newClients" color="var(--c-cyan,#00d4ff)" label={es?"Nuevos Clientes por Mes":"New Clients per Month"} />
          </div>
          <div style={{ background: "var(--c-surface,#0d1c2e)", border: "1px solid var(--c-border,#1a3550)", borderRadius: 12, padding: "1rem" }}>
            <BarChart data={monthlyData} dataKey="newPolicies" color="var(--c-green,#00ff88)" label={es?"Nuevas Pólizas por Mes":"New Policies per Month"} />
          </div>
        </div>
        {/* Cumulative Growth */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: ".75rem", marginBottom: "1rem" }}>
          <div style={{ background: "var(--c-surface,#0d1c2e)", border: "1px solid var(--c-border,#1a3550)", borderRadius: 12, padding: "1rem" }}>
            <BarChart data={monthlyData} dataKey="cumClients" color="#48cae4" label={es?"Clientes Acumulados":"Cumulative Clients"} />
          </div>
          <div style={{ background: "var(--c-surface,#0d1c2e)", border: "1px solid var(--c-border,#1a3550)", borderRadius: 12, padding: "1rem" }}>
            <BarChart data={monthlyData} dataKey="cumPolicies" color="#06d6a0" label={es?"Pólizas Acumuladas":"Cumulative Policies"} />
          </div>
        </div>
        {/* Month over Month table */}
        <div style={{ background: "var(--c-surface,#0d1c2e)", border: "1px solid var(--c-border,#1a3550)", borderRadius: 12, padding: "1rem", overflowX: "auto" }}>
          <div style={{ fontSize: ".7rem", fontWeight: 700, color: "var(--c-cyan,#00d4ff)", textTransform: "uppercase", letterSpacing: ".08em", fontFamily: "var(--font-mono)", marginBottom: ".5rem" }}>{es?"Resumen Mensual":"Monthly Summary"}</div>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: ".72rem" }}>
            <thead>
              <tr style={{ borderBottom: "1px solid var(--c-border,#1a3550)" }}>
                {[es?"Mes":"Month",es?"Nuevos Cl.":"New Cl.",es?"Nuevas Pol.":"New Pol.",es?"Total Cl.":"Total Cl.",es?"Total Pol.":"Total Pol."].map((h,i)=>(
                  <th key={i} style={{ padding: ".4rem .5rem", textAlign: i===0?"left":"right", color: "var(--c-text3,#45688a)", fontWeight: 700, fontFamily: "var(--font-mono)", fontSize: ".6rem", textTransform: "uppercase", letterSpacing: ".06em" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {monthlyData.map((m,i) => (
                <tr key={i} style={{ borderBottom: "1px solid var(--c-border,#1a3550)", background: i===now.getMonth()?"var(--c-cyan-dim)":"transparent" }}>
                  <td style={{ padding: ".35rem .5rem", fontWeight: 600, color: i===now.getMonth()?"var(--c-cyan,#00d4ff)":"var(--c-white,#eef6ff)" }}>{m.name} {i===now.getMonth()?"◀":""}</td>
                  <td style={{ padding: ".35rem .5rem", textAlign: "right", color: m.newClients>0?"var(--c-cyan,#00d4ff)":"var(--c-text3,#45688a)", fontWeight: m.newClients>0?700:400, fontFamily: "var(--font-mono)" }}>{m.newClients>0?`+${m.newClients}`:"-"}</td>
                  <td style={{ padding: ".35rem .5rem", textAlign: "right", color: m.newPolicies>0?"var(--c-green,#00ff88)":"var(--c-text3,#45688a)", fontWeight: m.newPolicies>0?700:400, fontFamily: "var(--font-mono)" }}>{m.newPolicies>0?`+${m.newPolicies}`:"-"}</td>
                  <td style={{ padding: ".35rem .5rem", textAlign: "right", fontFamily: "var(--font-mono)", color: "var(--c-text,#c8dff0)" }}>{m.cumClients}</td>
                  <td style={{ padding: ".35rem .5rem", textAlign: "right", fontFamily: "var(--font-mono)", color: "var(--c-text,#c8dff0)" }}>{m.cumPolicies}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </>)}

      {/* ══════ TEAM TAB ══════ */}
      {tab === "team" && (<>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(280px,1fr))", gap: ".75rem" }}>
          {(users||[]).map(user => {
            const isSeat = user.role === "assistant";
            // Tasks and hours attribute to ANY user (agent or seat).
            const userTasks = allTasks.filter(t => t.assignedTo === user.id || t.assignedTo === user.name).length;
            const userTasksDone = allTasks.filter(t => (t.assignedTo === user.id || t.assignedTo === user.name) && t.status === "done").length;
            // Hours worked in the selected period (timelog entries are date-only).
            const userHours = allTime
              .filter(e => e.userId === user.id && inPeriod(e.date))
              .reduce((s, e) => s + (Number(e.hours) || 0), 0);

            // A SEAT works ON BEHALF of its agent — clients/policies/sales
            // always belong to the agent, never the seat. So a seat is measured
            // by WORK (tasks) + TIME (hours), not by clients/sales.
            let stats;
            if (isSeat) {
              stats = [
                { l: es?"Tasks Pendientes":"Pending Tasks", v: userTasks - userTasksDone, c: "var(--c-red,#ff3d6b)" },
                { l: es?"Tasks Hechas":"Tasks Done", v: userTasksDone, c: "var(--c-green,#00ff88)" },
                { l: es?"Horas (periodo)":"Hours (period)", v: userHours.toFixed(1), c: "var(--c-cyan,#00d4ff)" },
              ];
            } else {
              // Agent/owner: clients attributed by agentId; policies via their
              // client; leads/conversions by the agent's display name.
              const myClientIds = new Set(allClients.filter(c => c.agentId === user.id).map(c => c.id));
              const userClients = myClientIds.size;
              const userPolicies = allPolicies.filter(p => p.status === "active" && myClientIds.has(p.clientId)).length;
              const userLeads = allLeads.filter(l => l.assignedTo === user.name).length;
              const userConv = allLeads.filter(l => l.assignedTo === user.name && l.status === "converted").length;
              stats = [
                { l: es?"Clientes":"Clients", v: userClients, c: "var(--c-cyan,#00d4ff)" },
                { l: es?"Pólizas":"Policies", v: userPolicies, c: "var(--c-green,#00ff88)" },
                { l: "Leads", v: userLeads, c: "#f472b6" },
                { l: es?"Convertidos":"Converted", v: userConv, c: "var(--c-amber,#ffb700)" },
                { l: es?"Tasks Hechas":"Tasks Done", v: userTasksDone, c: "var(--c-green,#00ff88)" },
                { l: es?"Horas (periodo)":"Hours (period)", v: userHours.toFixed(1), c: "var(--c-cyan,#00d4ff)" },
              ];
            }
            const parentAgent = isSeat && user.parentAgentId ? (users||[]).find(u => u.id === user.parentAgentId) : null;
            const roleLabel = isSeat ? (es ? "Asiento" : "Seat") : (user.role || "");
            return (
              <div key={user.id} style={{ background: "var(--c-surface,#0d1c2e)", border: "1px solid var(--c-border,#1a3550)", borderRadius: 12, padding: "1rem" }}>
                <div style={{ display: "flex", alignItems: "center", gap: ".6rem", marginBottom: ".75rem" }}>
                  <div style={{ width: 40, height: 40, borderRadius: "50%", background: user.color, display: "flex", alignItems: "center", justifyContent: "center", fontSize: ".8rem", fontWeight: 700, color: "#000", border: "2px solid var(--c-border,#1a3550)" }}>{user.initials}</div>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: ".85rem", color: "var(--c-white,#eef6ff)" }}>{user.name}</div>
                    <div style={{ fontSize: ".62rem", color: "var(--c-text3,#45688a)", fontFamily: "var(--font-mono)", textTransform: "uppercase" }}>{roleLabel}{parentAgent ? ` · ${es ? "atiende a" : "supports"} ${parentAgent.name}` : ""}</div>
                  </div>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: ".3rem" }}>
                  {stats.map((s, si) => (
                    <div key={si} style={{ background: "var(--c-surface2,#112538)", borderRadius: 6, padding: ".35rem .5rem", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <span style={{ fontSize: ".62rem", color: "var(--c-text3,#45688a)" }}>{s.l}</span>
                      <span style={{ fontFamily: "var(--font-display)", fontSize: ".85rem", fontWeight: 800, color: s.c }}>{s.v}</span>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </>)}

      {/* ══════ FINANCIAL TAB ══════ */}
      {tab === "financial" && (<>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(180px,1fr))", gap: ".5rem", marginBottom: "1rem" }}>
          <KPI icon="" value={`$${money0(totalCommissions)}`} label={es?"Comisiones Totales":"Total Commissions"} color="var(--c-green,#00ff88)" />
          <KPI icon="" value={`$${money0(totalExpenses)}`} label={es?"Gastos Totales":"Total Expenses"} color="var(--c-red,#ff3d6b)" />
          <KPI icon="" value={`$${money0(netProfit)}`} label={es?"Ganancia Neta":"Net Profit"} color={netProfit >= 0 ? "var(--c-green,#00ff88)" : "var(--c-red,#ff3d6b)"} />
          <KPI icon="" value={`$${money0(annualPremium)}`} label={es?"Book of Business (Anual)":"Book of Business (Annual)"} color="var(--c-amber,#ffb700)" />
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: ".75rem" }}>
          <div style={{ background: "var(--c-surface,#0d1c2e)", border: "1px solid var(--c-border,#1a3550)", borderRadius: 12, padding: "1rem" }}>
            <BarChart data={monthlyData} dataKey="commissions" color="var(--c-green,#00ff88)" label={es?"Comisiones por Mes":"Commissions per Month"} fmtVal={(v) => `$${money0(v)}`} />
          </div>
          <div style={{ background: "var(--c-surface,#0d1c2e)", border: "1px solid var(--c-border,#1a3550)", borderRadius: 12, padding: "1rem" }}>
            <BarChart data={monthlyData} dataKey="expenses" color="var(--c-red,#ff3d6b)" label={es?"Gastos por Mes":"Expenses per Month"} fmtVal={(v) => `$${money0(v)}`} />
          </div>
        </div>
        {/* Profit/Loss Summary */}
        <div style={{ marginTop: ".75rem", background: "var(--c-surface,#0d1c2e)", border: "1px solid var(--c-border,#1a3550)", borderRadius: 12, padding: "1rem" }}>
          <div style={{ fontSize: ".7rem", fontWeight: 700, color: "var(--c-amber,#ffb700)", textTransform: "uppercase", letterSpacing: ".08em", fontFamily: "var(--font-mono)", marginBottom: ".5rem" }}>{es ? "P&L — Profit & Loss" : "P&L — Profit & Loss"}</div>
          {[
            { l: es?"Premium Mensual (Book)":"Monthly Premium (Book)", v: `$${money2(totalPremium)}`, c: "var(--c-cyan,#00d4ff)", bold: false },
            { l: es?"Premium Anual (Book)":"Annual Premium (Book)", v: `$${money2(annualPremium)}`, c: "var(--c-cyan,#00d4ff)", bold: true },
            { l: "", v: "", c: "", sep: true },
            { l: es?"(+) Comisiones Recibidas":"(+) Commissions Received", v: `$${money2(totalCommissions)}`, c: "var(--c-green,#00ff88)", bold: false },
            { l: es?"(-) Gastos Operacionales":"(-) Operating Expenses", v: `-$${money2(totalExpenses)}`, c: "var(--c-red,#ff3d6b)", bold: false },
            { l: "", v: "", c: "", sep: true },
            { l: es?"(=) GANANCIA NETA":"(=) NET PROFIT", v: `$${money2(netProfit)}`, c: netProfit >= 0 ? "var(--c-green,#00ff88)" : "var(--c-red,#ff3d6b)", bold: true },
            { l: es?"Margen de Ganancia":"Profit Margin", v: totalCommissions > 0 ? `${((netProfit / totalCommissions) * 100).toFixed(0)}%` : "N/A", c: "#c084fc", bold: false },
          ].map((item, i) => item.sep ? (
            <div key={i} style={{ borderTop: "1px solid var(--c-border,#1a3550)", margin: ".3rem 0" }} />
          ) : (
            <div key={i} style={{ display: "flex", justifyContent: "space-between", padding: ".35rem .5rem", background: item.bold ? "var(--c-surface2,#112538)" : "transparent", borderRadius: 6, marginBottom: 2 }}>
              <span style={{ fontSize: item.bold ? ".78rem" : ".72rem", color: item.bold ? "var(--c-white,#eef6ff)" : "var(--c-text2,#7aa3c0)", fontWeight: item.bold ? 700 : 400 }}>{item.l}</span>
              <span style={{ fontSize: item.bold ? ".9rem" : ".78rem", fontWeight: 800, color: item.c, fontFamily: "var(--font-display)" }}>{item.v}</span>
            </div>
          ))}
        </div>
      </>)}
    </div>
  );
}
